import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Eye,
  Fingerprint,
  KeyRound,
  ScrollText,
  ShieldCheck,
  Sparkles,
  UserCheck,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@eduviaos/ui-kit";
import { useEnabledModules } from "@/hooks/useEnabledModules";

/**
 * Phase E2c — IA Security landing page.
 *
 * Sibling of SettingsPage — same rationale (sidebar entry shipped in
 * Phase D0c, route was never wired). Surfaces what the IA can expect
 * from the platform's security posture today and what's coming.
 *
 * The "What's enforced today" card lists guarantees that are already
 * live (refresh-token rotation, OWASP cookie flags, cross-tenant
 * cookie defense, password hashing) so the principal can answer
 * compliance questions without filing a support ticket.
 */

interface SecurityCard {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  status: string;
}

const SECURITY_CARDS: SecurityCard[] = [
  {
    icon: ShieldCheck,
    title: "What's enforced today",
    description:
      "Refresh tokens rotate on every login refresh, session cookies ship with HttpOnly + Secure + SameSite-Lax flags, every session is scoped to your institution (cross-institution cookie replays are rejected), and passwords are stored with industry-standard memory-hard hashing. Every authentication event is written to an immutable audit log.",
    status: "Available now",
  },
  {
    icon: Fingerprint,
    title: "Multi-factor authentication",
    description:
      "TOTP via authenticator apps and WebAuthn (passkeys / hardware keys). Will be enforceable per-role so admins can require step-up auth for sensitive operations.",
    status: "Coming soon",
  },
  {
    icon: KeyRound,
    title: "Password policy",
    description:
      "Configure minimum length, breach-corpus checking, and rotation cadence. Defaults today are 12 characters minimum, no rotation forced, breach-check disabled — explicit overrides will live here.",
    status: "Coming soon",
  },
  {
    icon: UserCheck,
    title: "Session management",
    description:
      "Inspect and revoke active sessions by user, force-logout on role change, configure idle / absolute session lifetimes, and review impossible-travel anomalies.",
    status: "Coming soon",
  },
  {
    icon: Eye,
    title: "Login activity",
    description:
      "Read-only timeline of successful logins, failed attempts, MFA challenges, and IP / user-agent changes — sourced from the audit log with privacy-safe redaction.",
    status: "Coming soon",
  },
  {
    icon: ScrollText,
    title: "Audit log export",
    description:
      "DPDPA-compliant CSV / JSON export of authentication, RBAC, and data-access audit events for compliance reviews.",
    status: "Coming soon",
  },
];

const STUDY_CENTRE_ENFORCED =
  "Sign-ins refresh securely, session cookies are locked to this browser and to your centre, and passwords are stored with strong one-way hashing. Every sign-in is recorded in a tamper-proof log.";

export function SecurityPage() {
  const { vertical } = useEnabledModules();
  const studyCentre = vertical === "study_centre";
  const cards = studyCentre
    ? SECURITY_CARDS.filter((card) => card.status === "Available now").map((card) => ({
        ...card,
        description: STUDY_CENTRE_ENFORCED,
      }))
    : SECURITY_CARDS;

  return (
    <div className="mx-auto max-w-5xl py-10">
      <Button variant="ghost" size="sm" asChild className="mb-6">
        <Link to="/dashboard">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to dashboard
        </Link>
      </Button>

      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Security</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-relaxed">
          {studyCentre
            ? "How your centre's accounts and sign-ins are protected."
            : "Authentication policy, session controls, and audit visibility for your institution. The first card describes guarantees that are enforced platform-wide today; the rest land on this surface progressively."}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {cards.map((card) => {
          const Icon = card.icon;
          const isLive = card.status === "Available now";
          return (
            <Card key={card.title} className="border-border/80 bg-card/60 backdrop-blur">
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="bg-primary/10 text-primary grid size-10 shrink-0 place-items-center rounded-lg"
                  >
                    <Icon className="size-5" />
                  </span>
                  <div className="min-w-0">
                    <CardTitle className="text-base font-semibold">{card.title}</CardTitle>
                    <CardDescription
                      className={
                        "mt-1 inline-flex items-center gap-1 text-xs " +
                        (isLive
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-muted-foreground")
                      }
                    >
                      <Sparkles aria-hidden="true" className="size-3" />
                      {card.status}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 text-sm leading-relaxed">{card.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
